if (!customElements.get('besneaker-shelf')) {
  customElements.define('besneaker-shelf', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-shelf-track]');
      this.pool = this.querySelector('[data-shelf-pool]');
      if (!this.track || !this.pool) return;
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = false;
      const cards = Array.from(this.pool.content.children);
      if (this.dataset.random === 'true') {
        for (let i = cards.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [cards[i], cards[j]] = [cards[j], cards[i]];
        }
        const count = Math.max(2, Math.min(8, Number(this.dataset.limit) || 8));
        this.track.replaceChildren(...cards.slice(0, count).map(card => card.cloneNode(true)));
      }
      this.querySelector('[data-shelf-controls]').hidden = false;
      this.pauseButton = this.querySelector('[data-shelf-pause]');
      this.pauseButton.hidden = this.dataset.autoplay !== 'true' || this.reduced.matches;
      this.querySelector('[data-shelf-prev]').addEventListener('click', () => this.move(-1), options);
      this.querySelector('[data-shelf-next]').addEventListener('click', () => this.move(1), options);
      this.pauseButton.addEventListener('click', () => {
        this.paused = !this.paused;
        this.pauseButton.setAttribute('aria-label', this.paused ? 'Reanudar avance automático' : 'Pausar avance automático');
        this.pauseButton.setAttribute('aria-pressed', String(this.paused));
        this.start();
      }, options);
      this.addEventListener('mouseenter', () => { this.hovered = true; this.stop(); }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; this.start(); }, options);
      this.addEventListener('focusin', () => this.stop(), options);
      this.addEventListener('focusout', () => setTimeout(() => this.isConnected && this.start(), 0), options);
      this.track.addEventListener('pointerdown', () => { this.dragging = true; this.stop(); }, options);
      document.addEventListener('pointerup', () => { if (this.dragging) { this.dragging = false; this.start(); } }, options);
      document.addEventListener('pointercancel', () => { this.dragging = false; this.start(); }, options);
      document.addEventListener('visibilitychange', () => this.start(), options);
      this.reduced.addEventListener('change', () => {
        this.pauseButton.hidden = this.dataset.autoplay !== 'true' || this.reduced.matches;
        this.start();
      }, options);
      this.track.addEventListener('keydown', e => {
        if (e.target === this.track && ['ArrowLeft','ArrowRight'].includes(e.key)) {
          e.preventDefault(); this.move(e.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);
      this.start();
      if (this.dataset.arrivalsSource) {
        this.querySelector('[data-arrivals-retry]')?.addEventListener('click', () => this.loadArrivals(), options);
        this.loadArrivals();
      }
    }
    async loadArrivals() {
      if (this.loadingArrivals) return;
      this.loadingArrivals = true;
      const retry = this.querySelector('[data-arrivals-retry]');
      if (retry) retry.hidden = true;
      const seeds = [], fresh = [], seen = new Set(), visited = new Set();
      let url = this.dataset.arrivalsSource;
      this.setAttribute('aria-busy', 'true');
      try {
        while (url && !this.abort.signal.aborted) {
          const target = new URL(url, location.href);
          if (target.origin !== location.origin || visited.has(target.href)) throw new Error('Invalid arrivals page');
          target.searchParams.set('view', 'bn-arrivals');
          target.searchParams.set('sort_by', 'created-descending');
          if (visited.has(target.href)) throw new Error('Repeated arrivals page');
          visited.add(target.href);
          const response = await fetch(target.href, {signal: this.abort.signal, headers: {Accept: 'text/html'}});
          if (!response.ok) throw new Error('Arrivals unavailable');
          const page = new DOMParser().parseFromString(await response.text(), 'text/html');
          const results = page.querySelector('[data-bn-arrivals-page]');
          if (!results) throw new Error('Arrivals markup unavailable');
          results.querySelectorAll('[data-arrival-item]').forEach(item => {
            const card = item.querySelector('[data-product-id]');
            if (!card || seen.has(card.dataset.productId)) return;
            seen.add(card.dataset.productId);
            (item.dataset.new === 'true' ? fresh : seeds).push(card);
          });
          const products = fresh.length ? fresh : seeds;
          if (products.length) {
            const left = this.track.scrollLeft;
            this.track.replaceChildren(...products.map(card => card.cloneNode(true)));
            this.track.scrollLeft = left;
          }
          url = results.querySelector('[data-arrivals-next]')?.getAttribute('href');
        }
      } catch (error) {
        if (error.name !== 'AbortError' && retry) retry.hidden = false;
      } finally {
        this.loadingArrivals = false;
        this.removeAttribute('aria-busy');
      }
    }
    disconnectedCallback() { this.stop(); this.abort?.abort(); }
    stop() { clearInterval(this.timer); }
    start() {
      this.stop();
      if (this.dataset.autoplay !== 'true' || this.paused || this.hovered || this.dragging || this.reduced.matches || document.hidden || this.contains(document.activeElement)) return;
      this.timer = setInterval(() => this.move(1), Math.max(2000, Number(this.dataset.interval) || 2000));
    }
    move(direction) {
      const first = this.track.firstElementChild;
      if (!first) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 1) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
      const step = first.getBoundingClientRect().width + gap;
      let target = this.track.scrollLeft + direction * step;
      if (direction > 0 && this.track.scrollLeft >= max - 3) target = 0;
      if (direction < 0 && this.track.scrollLeft <= 3) target = max;
      this.track.scrollTo({left: Math.max(0, Math.min(max, target)), behavior:this.reduced.matches ? 'instant' : 'smooth'});
    }
  });
}
